import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BrandService } from '../../services/brand-config.service';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class GtmAnalyticsService {
  private isEnabled: boolean = false;

  constructor() {
    this.isEnabled = environment.production;

    if (this.isEnabled) {
      this.initializeDataLayer();
    }else {
      console.log('GtmAnalyticsService: dataLayer STOPPED');
    }
  }

  private initializeDataLayer(): void {
    window.dataLayer = window.dataLayer || [];
    console.log('GtmAnalyticsService: dataLayer initialized');
  }

  private pushEvent(eventData: any): void {
    if (!this.isEnabled) {
      console.log('GtmAnalyticsService: event not sent (disabled - not production Markable)', eventData);
      return;
    }

    // GTM (Meta pixel / Google Ads) loads only on the separate marketing opt-in.
    // Without it, do not queue events either, so nothing replays if GTM loads later.
    if ((window as any).__marketingConsent !== 'granted') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventData);
  }

  trackStartedTest(): void {
    this.pushEvent({
      event: 'started_test',
    });
  }

  trackFinishedScan(): void {
    this.pushEvent({
      event: 'finished_scan',
    });
  }

  /** A store badge / QR entry point was used, and from WHERE: the end of her
   *  report, the profile row, or the dashboard card (app-download spec). */
  trackAppStoreClicked(placement: 'report' | 'profile' | 'home' | 'routine', store: 'ios' | 'android' | 'qr'): void {
    this.pushEvent({
      event: 'app_store_clicked',
      placement,
      store,
    });
  }

  trackPaywallViewed(): void {
    this.pushEvent({
      event: 'paywall_viewed',
    });
  }

  trackPricingViewed(): void {
    this.pushEvent({
      event: 'pricing_viewed',
    });
  }

  trackCompletedPayment(data: {
    value: number;
    currency: string;
    plan_type: 'one_time' | 'subscription';
    plan_name: string;
  }): void {
    this.pushEvent({
      event: 'completed_payment',
      value: data.value,
      currency: data.currency,
      plan_type: data.plan_type,
      plan_name: data.plan_name,
    });
  }

  // Scan ids / invite tokens must never reach analytics: they tie a health-app page to a person.
  private scrubPath(path: string): string {
    return (path || "")
      .split(/[?#]/)[0]
      .split("/")
      .map((seg) =>
        /^[0-9a-f-]{8,}$/i.test(seg) && /\d/.test(seg) ? ":id"
          : /^\d+$/.test(seg) ? ":id"
          : /^[A-Za-z0-9_-]{20,}$/.test(seg) ? ":id"
          : seg)
      .join("/");
  }

  // Last pushed clean URL, used as page_referrer for the next in-app screen.
  private lastCleanUrl: string | null = null;
  // Static brand title captured at boot. document.title can carry personal
  // data (the clinician report sets it to a filename with her name).
  private readonly baseTitle = (document.title || "").trim();

  /** Same scrub for any URL: same-origin -> origin + scrubbed path (no query,
   *  no hash); other origins -> origin only; unparsable -> "". */
  private scrubUrl(url: string): string {
    if (!url) return "";
    try {
      const u = new URL(url, location.origin);
      if (u.origin !== location.origin) return u.origin;
      return u.origin + this.scrubPath(u.pathname);
    } catch {
      return "";
    }
  }

  /** Fired on EVERY internal screen change (Router NavigationEnd) and on boot.
   *  Event name and param names unchanged so existing GTM triggers keep working. */
  trackPageView(pagePath: string, _pageTitle?: string): void {
    const clean = this.scrubPath(pagePath);
    const pageLocation = location.origin + clean;
    const referrer = this.lastCleanUrl ?? this.scrubUrl(document.referrer);
    this.lastCleanUrl = pageLocation;
    this.pushEvent({
      event: "page_view",
      page_path: clean,
      page_location: pageLocation,
      page_referrer: referrer,
      page_title: this.baseTitle,
    });
  }
}
